import { Icon } from "../ui/Icon";
import "./MrpUyarilari.css";

/**
 * MRP MOTORUNUN HESABA KATAMADIKLARI — `runMrp`ın döndürdüğü üç uyarı listesinin GÖSTERİMİ.
 *
 * ⚠ SESSİZLİK YASAĞI: motor artık "hesaplayamadım"ı ayrı ayrı bildiriyor (mrp.ts →
 * `recetesizUrunler` / `atlananKalemler` / `patlatilamayanBilesenler`). Bu listeler ekranda
 * okunmazsa boş öneri listesi "her şey yeterli" diye okunur — kullanıcının 29 Ağu 2026'da
 * canlıda takıldığı kusurun ta kendisi ("eksik analizi yapılamadı, stokta her şey var görünüyor").
 *
 * ⚠ AYNA KURALI: aynı motorun aynı cevabını İKİ ekran gösteriyor — sipariş kartı
 * (`orders/MaterialNeeds`) ve genel MRP ekranı (`pages/Mrp`). Gösterim ayrı ayrı yazılsaydı
 * biri güncellenip diğeri unutulurdu; nitekim uyarılar önce yalnız sipariş kartına yazılmış,
 * genel MRP ekranı susmaya devam etmişti. Tek bileşen = tek metin = tek davranış.
 */

export interface MrpUyariVerisi {
  recetesizUrunler: Array<{ itemKind: string; itemId: string; code: string; name: string; qty: number }>;
  atlananKalemler: Array<{ itemKind: string; itemId: string; code: string; name: string; qty: number }>;
  patlatilamayanBilesenler: Array<{ itemKind: string; itemId: string; code: string; name: string; qty: number; uom: string; recetesiVar: boolean }>;
}

/**
 * MRP DIŞI KALEM — kart türü "MRP'ye dahil etme" işaretli ham madde (GENEL GİDER, HİZMET gibi stok
 * takibi olmayan gider kalemleri; mrp.ts `mrpDisiKalemler`). Satın alma eksiği SAYILMAZ ama brüt
 * ihtiyacıyla gösterilir — sessizce kaybolmaz (kullanıcı kararı, 2 Eki 2026).
 * ⚠ ŞEKİL SABİT: genel MRP ekranı (`pages/Mrp`) da aynı prop'u geçer.
 */
export interface MrpDisiKalem { code: string; name: string; uom: string; qty: number; tur: string }

const fmt = (n: number) => new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);

/**
 * ═══ ELDE — TANIM VE ALT NOTU (mrp.ts `eldeHaritasi`, kullanıcı kararı 2 Eki 2026) ═══
 *
 * "Elde" artık KULLANILABİLİR NET stok: SKT'si geçmemiş released/consumed lotların net toplamı,
 * eksi bakiyeler düşülmüş, 0'ın altına inmez. SKT'si geçmiş ve QC/karantina/red lotlardaki stok
 * elde SAYILMAZ ama AYRICA gösterilir; eksi bakiyelerin kullanılabilir stoğu aştığı kısım "kayıt
 * açığı"dır. Bu üç bilgi ekranda okunmazsa kullanıcı Stok ekranında gördüğü miktarla MRP'nin
 * "elde"sini karşılaştırıp hesabın yanlış olduğunu sanır (canlı olay: AMB-RSTRNT-01, 0 ↔ 25.760).
 *
 * ⚠ AYNA KURALI: tanım metni, etiketler ve görünürlük eşiği TEK yerde — sipariş kartı
 * (`orders/MaterialNeeds`), A4 MRP raporu (`pages/MrpReportPrint`) ve genel MRP ekranı (`pages/Mrp`)
 * buradan okur. Genel MRP ekranında bir dönem ayrı bir kopya vardı (eşik `> 0`, farklı renk ve etiket):
 * aynı kalem iki ekranda farklı not taşıyordu. Kopya yazılmaz; `runMrp` alan adları EŞLENİR.
 */
export const MRP_ELDE_TANIMI =
  "kullanılabilir net stok; eksi bakiyeler düşülür; SKT geçmiş ve karantina/QC stok sayılmaz, ayrıca gösterilir";
export const MRP_KAYIT_ACIGI_TANIMI =
  "eksi bakiyelerin kullanılabilir stoğu aştığı kısım — elde 0'ın altına inmez ve açık satın alma ihtiyacına eklenmez; stok kaydı düzeltilmelidir";

/** Sunucu 3 haneye yuvarlar; 2 haneli gösterimde "0" okunacak kırıntı not olarak basılmaz. */
const ELDE_NOTU_ESIGI = 0.005;

export type MrpEldeNotTuru = "skt" | "karantina" | "acik";
export interface MrpEldeNotKaydi { tur: MrpEldeNotTuru; etiket: string; qty: number; aciklama: string }

/** Bir satırın elde alt notları — ekran ve baskı AYNI listeyi basar (etiket + eşik tek kaynak). */
export function mrpEldeNotlari(x: { sktGecmis: number; karantina: number; kayitAcigi: number }): MrpEldeNotKaydi[] {
  const out: MrpEldeNotKaydi[] = [];
  if (x.sktGecmis >= ELDE_NOTU_ESIGI) {
    out.push({ tur: "skt", etiket: "SKT geçmiş", qty: x.sktGecmis, aciklama: "SKT'si geçmiş lotlardaki stok — elde sayılmadı." });
  }
  if (x.karantina >= ELDE_NOTU_ESIGI) {
    out.push({ tur: "karantina", etiket: "Karantina/QC", qty: x.karantina, aciklama: "QC bekleyen / karantina / red lotlardaki stok — elde sayılmadı." });
  }
  if (x.kayitAcigi >= ELDE_NOTU_ESIGI) {
    out.push({ tur: "acik", etiket: "Kayıt açığı", qty: x.kayitAcigi, aciklama: "Eksi bakiyeler kullanılabilir stoğu aştı — stok kaydı düzeltilmeli." });
  }
  return out;
}

/**
 * Bir satır listesinde elde notu VAR MI, kayıt açığı VAR MI — "Elde/Eldeki" tanım dipnotunun ne zaman
 * (ve kayıt açığı tanımıyla birlikte mi) basılacağını söyler. Eşik `mrpEldeNotlari`ndan gelir: not
 * basılmayan kırıntı satır dipnotu da tetiklemez (sipariş kartı ve genel MRP ekranı AYNI kural).
 */
export function mrpEldeNotOzeti(satirlar: ReadonlyArray<{ sktGecmis: number; karantina: number; kayitAcigi: number }>): { notVar: boolean; acikVar: boolean } {
  let notVar = false;
  let acikVar = false;
  for (const s of satirlar) {
    const notlar = mrpEldeNotlari(s);
    if (notlar.length > 0) notVar = true;
    if (notlar.some((n) => n.tur === "acik")) acikVar = true;
  }
  return { notVar, acikVar };
}

/**
 * ELDE UYARISI — net ihtiyacı OLMAYAN (eksik/öneri listesine girmeyen) ama SKT geçmiş / karantina stoğu
 * ya da kayıt açığı taşıyan kalem (mrp.ts `eldeUyarilari`; sipariş uçlarında aynı alan adlarıyla).
 * Öneri listesi yalnız eksikleri gösterdiği için bu kalemler başka hiçbir yerde görünmez.
 * ⚠ ŞEKİL SABİT: genel MRP ekranı `runMrp`in `expiredQty/blockedQty/deficitQty` adlarını buraya EŞLER.
 */
export interface MrpEldeUyarisi { code: string; name: string; uom: string; sktGecmis: number; karantina: number; kayitAcigi: number }

/** Yalnız EKRANDA not basılacak kalemler — sunucu eşiği (0,0005) gösterim eşiğinden düşük; kırıntı satır boş kalmasın. */
function gorunurEldeUyarilari(liste: ReadonlyArray<MrpEldeUyarisi> | undefined): MrpEldeUyarisi[] {
  return (liste ?? []).filter((k) => mrpEldeNotlari(k).length > 0);
}

/**
 * "YETERLİ" METNİ — TEK KAYNAK. Genel MRP ekranı, sipariş kartı ve A4 MRP raporu eksik/öneri listesi
 * BOŞ çıktığında bu cümleyi basar (ayna kuralı: üç ekran aynı durumu aynı cümleyle söyler).
 *
 * ⚠ İDDİA DARALTILIR: MRP dışı (gider/hizmet) kalemler stokla HİÇ karşılaştırılmadı; elde uyarısı olan
 * kalemlerde stokta kullanılamayan miktar ya da kayıt açığı var. Bunlar varken düz "tüm hammaddeler
 * yeterli" demek, karşılaştırılmamış kalemler için de yeterlilik iddia etmek olurdu.
 *
 * ⚠ `mrpHesabiEksik`in YERİNE GEÇMEZ: hesap eksikse (reçetesiz ürün, atlanan satır, patlatılamayan dal)
 * bu metin HİÇ basılmaz — çağıran önce `mrpHesabiEksik`e bakar. MRP dışı / elde uyarısı hesabı eksik
 * YAPMAZ, yalnız iddianın kapsamını daraltır.
 */
export function yeterliMetni({
  mrpDisiKalemler, eldeUyarilari,
}: {
  mrpDisiKalemler?: ReadonlyArray<unknown>;
  eldeUyarilari?: ReadonlyArray<MrpEldeUyarisi>;
}): string {
  const disiSayi = mrpDisiKalemler?.length ?? 0;
  const eldeSayi = gorunurEldeUyarilari(eldeUyarilari).length;
  if (disiSayi === 0 && eldeSayi === 0) return "Tüm hammaddeler yeterli — ek tedarik gerekmiyor.";
  const ekler: string[] = [];
  if (disiSayi > 0) ekler.push(`${fmt(disiSayi)} MRP dışı kalem stokla karşılaştırılmadı`);
  if (eldeSayi > 0) ekler.push(`${fmt(eldeSayi)} kalemde elde sayılmayan stok ya da kayıt açığı var`);
  return `${disiSayi > 0 ? "MRP'ye dahil hammaddelerde" : "Hammaddelerde"} eksik yok — ${ekler.join(" · ")} (ayrıntı yukarıda).`;
}

const ELDE_NOT_IKONU: Record<MrpEldeNotTuru, string> = { skt: "clock", karantina: "lock", acik: "alertTriangle" };

/**
 * Elde hücresinin ALTINA basılan küçük not (renk + ikon + metin). Yeni sütun AÇILMAZ — tablo
 * genişliği değişmesin. `yerlesim="satir"`: mobil kartta yan yana, sararak.
 */
export function MrpEldeNotu({
  sktGecmis, karantina, kayitAcigi, uom, yerlesim = "hucre",
}: {
  sktGecmis: number; karantina: number; kayitAcigi: number; uom: string;
  yerlesim?: "hucre" | "satir";
}) {
  const notlar = mrpEldeNotlari({ sktGecmis, karantina, kayitAcigi });
  if (notlar.length === 0) return null;
  return (
    <ul className={`mn-elde-not ${yerlesim}`} aria-label="Elde sayılmayan stok">
      {notlar.map((n) => (
        <li key={n.tur} className={n.tur} title={n.aciklama}>
          <Icon name={ELDE_NOT_IKONU[n.tur]} size={12} />
          <span>{n.etiket}:</span>
          <span className="v">{fmt(n.qty)} {uom}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * HESAP EKSİK Mİ? — true ise HİÇBİR ekran "tüm hammaddeler yeterli" DEMEMELİ.
 * Yeterlilik iddiası ancak talebin TAMAMI patlatılabildiyse kurulabilir.
 */
export function mrpHesabiEksik(veri?: MrpUyariVerisi): boolean {
  if (!veri) return false;
  return (
    veri.recetesizUrunler.length > 0 ||
    veri.atlananKalemler.length > 0 ||
    veri.patlatilamayanBilesenler.length > 0
  );
}

export function MrpUyarilari({
  veri, mrpDisiKalemler, eldeUyarilari,
}: {
  veri?: MrpUyariVerisi;
  /** Doluysa "satın alma eksiğine katılmadı" bilgi kutusu basılır (bkz. `MrpDisiKalem`). */
  mrpDisiKalemler?: Array<{ code: string; name: string; uom: string; qty: number; tur: string }>;
  /** Doluysa "Eksiği olmayan ama dikkat gereken kalemler" bilgi kutusu basılır (bkz. `MrpEldeUyarisi`). */
  eldeUyarilari?: ReadonlyArray<MrpEldeUyarisi>;
}) {
  const disi = mrpDisiKalemler ?? [];
  const elde = gorunurEldeUyarilari(eldeUyarilari);
  if (!veri && disi.length === 0 && elde.length === 0) return null;
  return (
    <>
      {veri ? <HesapUyarilari veri={veri} /> : null}
      {disi.length > 0 ? <MrpDisiKutusu kalemler={disi} /> : null}
      {elde.length > 0 ? <EldeUyariKutusu kalemler={elde} /> : null}
    </>
  );
}

/**
 * EKSİK YOK AMA DİKKAT — BİLGİ kutusu: bu kalemlerde satın alma eksiği ÇIKMADI (öneri/eksik listesinde
 * yoklar) ama stokta kullanılamayan miktar (SKT geçmiş · karantina/QC) ya da kayıt açığı var.
 * Hesabı EKSİK yapmaz (`mrpHesabiEksik`e GİRMEZ); sessizce kaybolmasın diye söylenir. Kalem başına notlar
 * tablodaki elde notuyla AYNI bileşenden (`MrpEldeNotu`) — aynı eşik, renk, ikon, metin.
 */
function EldeUyariKutusu({ kalemler }: { kalemler: MrpEldeUyarisi[] }) {
  return (
    <div className="mn-msg info mn-bilgi mn-elde-uyari" role="note">
      <Icon name="eye" size={14} />
      <div className="mn-bilgi-govde">
        {/* Kapsam başlıkta: kutu eksik listesinin yanında da basılır — genel "eksik yok" iddiası gibi okunmasın (V8). */}
        <b>Eksiği olmayan ama dikkat gereken kalemler: elde sayılmayan stok ya da kayıt açığı</b>
        <span className="mn-bilgi-ack">
          Bu kalemlerde satın alma eksiği çıkmadı; aşağıdaki miktarlar elde SAYILMADI. SKT&apos;si geçmiş ve
          karantina/QC stok için imha ya da QC kararı, kayıt açığı için stok düzeltmesi gerekir.
        </span>
        <ul className="mn-bilgi-liste mn-elde-uyari-liste">
          {kalemler.map((k, i) => (
            <li key={`${k.code}-${i}`}>
              <span className="mn-elde-uyari-ad">
                {k.name} <span className="mn-kod mono">{k.code}</span>
              </span>
              <MrpEldeNotu sktGecmis={k.sktGecmis} karantina={k.karantina} kayitAcigi={k.kayitAcigi} uom={k.uom} yerlesim="satir" />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * MRP DIŞI KALEMLER — BİLGİ kutusu (uyarı değil): bu kalemler hesabı EKSİK yapmaz, bilinçli olarak
 * dışarıda tutulur. Bu yüzden `mrpHesabiEksik`e GİRMEZ; yalnız sessizce kaybolmasınlar diye
 * brüt ihtiyaçlarıyla, kart türüne göre gruplanıp yazılır.
 */
function MrpDisiKutusu({ kalemler }: { kalemler: MrpDisiKalem[] }) {
  const turler = new Map<string, MrpDisiKalem[]>();
  for (const k of kalemler) {
    const l = turler.get(k.tur) ?? [];
    l.push(k);
    turler.set(k.tur, l);
  }
  return (
    <div className="mn-msg info mn-bilgi mn-disi" role="note">
      <Icon name="info" size={14} />
      <div className="mn-bilgi-govde">
        <b>Stok takibi olmayan gider kalemleri satın alma eksiğine katılmadı</b>
        <span className="mn-bilgi-ack">
          Kart türü “MRP&apos;ye dahil etme” olarak işaretli. Miktarlar bu talebin brüt ihtiyacıdır;
          stok ve açık satın alma siparişleriyle karşılaştırılmadı.
        </span>
        <ul className="mn-bilgi-liste">
          {[...turler].map(([tur, ks]) => (
            <li key={tur}>
              <span className="mn-disi-tur">{tur}:</span>{" "}
              {ks.map((k, i) => (
                <span key={`${k.code}-${i}`}>
                  {i > 0 ? " · " : ""}
                  {k.name} <span className="mn-kod mono">{k.code}</span>{" "}
                  <span className="mono mn-disi-qty">{fmt(k.qty)} {k.uom}</span>
                </span>
              ))}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function HesapUyarilari({ veri }: { veri: MrpUyariVerisi }) {
  return (
    <>
      {veri.recetesizUrunler.length > 0 ? (
        <div className="mn-msg warn" role="alert">
          <Icon name="alertTriangle" size={14} />
          <span>
            <b>İhtiyaç hesaplanamadı</b> — şu ürünlerin etkin reçetesi yok:{" "}
            {veri.recetesizUrunler.map((u) => `${u.name} (${u.code})`).join(" · ")}.
            Reçete tanımlanmadan bu ürünlerin hammadde ihtiyacı bilinemez.
          </span>
        </div>
      ) : null}

      {veri.atlananKalemler.length > 0 ? (
        <div className="mn-msg warn">
          <Icon name="info" size={14} />
          <span>
            Şu satırlar patlatmaya girmedi (ham madde satırının reçetesi olmaz):{" "}
            {veri.atlananKalemler.map((u) => `${u.name} (${u.code})`).join(" · ")}.
          </span>
        </div>
      ) : null}

      {/*
        ALT BİLEŞEN SESSİZLİĞİ: kök ürünün reçetesi olsa bile ağacın bir dalı (reçetesiz yarı
        mamul, döngü koruması ya da derinlik sınırı) patlatılamamış olabilir. O dalın ham
        maddeleri listede YOKTUR — aynı kusurun bir seviye alttaki hali. SÖYLENİR.
      */}
      {veri.patlatilamayanBilesenler.length > 0 ? (
        <div className="mn-msg warn" role="alert">
          <Icon name="alertTriangle" size={14} />
          <span>
            <b>Liste eksik</b> — şu bileşenler patlatılamadı, ham madde ihtiyaçları bu hesaba KATILMADI:{" "}
            {veri.patlatilamayanBilesenler.map((u, i) => (
              <span key={`${u.itemKind}:${u.itemId}:${u.uom}`}>
                {i > 0 ? " · " : ""}
                {u.name} <span className="mn-kod mono">{u.code}</span>{" "}
                <span className="mono">{fmt(u.qty)} {u.uom}</span>
                {u.recetesiVar ? " (patlatma sınırı: döngü/derinlik)" : " (etkin reçetesi yok)"}
              </span>
            ))}
            .
          </span>
        </div>
      ) : null}
    </>
  );
}

/**
 * "Eksik çıkmadı AMA hesap tam değil" satırı — yeterlilik iddiasının YERİNE geçer.
 * Öneri/eksik listesi boş ama `mrpHesabiEksik` true olduğunda gösterilir.
 */
export function MrpEksikHesapNotu({ metin }: { metin: string }) {
  return (
    <div className="mn-msg warn" role="alert">
      <Icon name="alertTriangle" size={14} />
      {metin}
    </div>
  );
}
